import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Bookmark, User, Calendar, Loader2, ArrowLeft, Share2, Printer, MessageSquare, Send, Trash2, Eye, CornerDownRight } from 'lucide-react';
import { useQuran } from '../context/QuranContext';
import { useAuth } from '../context/AuthContext';
import LoginPromptDialog from './LoginPromptDialog';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from './ui/card';
import VerseReferenceLink from './VerseReferenceLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StoryReader = () => {
    const { topicId } = useParams();
    const navigate = useNavigate();
    const { startRootBrowsing, goToVerse } = useQuran();
    const { user: currentUser, isAuthenticated, getAuthHeaders } = useAuth();
    const [topic, setTopic] = useState(null);
    const [verses, setVerses] = useState([]);
    const [verseData, setVerseData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [surahs, setSurahs] = useState([]);

    // Comments State
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [isPosting, setIsPosting] = useState(false);
    const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
    const [loadingComments, setLoadingComments] = useState(false);
    const [replyingToId, setReplyingToId] = useState(null);
    const [replyText, setReplyText] = useState('');

    // Author Related Topics
    const [authorTopics, setAuthorTopics] = useState([]);
    const [showAllAuthorTopics, setShowAllAuthorTopics] = useState(false);

    useEffect(() => {
        loadSurahs();
    }, []);

    useEffect(() => {
        if (topicId) {
            loadTopic();
            loadComments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topicId]);

    useEffect(() => {
        if (topic?.name) {
            document.title = `${topic.name} | Ai Quran`;
        } else {
            document.title = 'Ai Quran: Advanced Analysis & AI Insights';
        }
        return () => {
            document.title = 'Ai Quran: Advanced Analysis & AI Insights';
        };
    }, [topic]);

    const loadSurahs = async () => {
        try {
            const res = await axios.get(`${API}/surahs`);
            setSurahs(res.data?.chapters || []);
        } catch { /* ignore */ }
    };

    const loadTopic = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${API}/library/${topicId}`);
            const { topic: topicData, verses: topicVerses, more_from_author } = res.data;
            setTopic(topicData);
            setVerses(topicVerses || []);
            setAuthorTopics(more_from_author || []);
            setShowAllAuthorTopics(false); // Reset on new topic

            // Fetch Arabic text and translations sequentially to avoid API rate limits from Free providers
            const verseDataMap = {};
            for (const v of (topicVerses || [])) {
                try {
                    const vRes = await axios.get(`${API}/verse/${v.verse_key}`, {
                        params: { translation_ids: 'en.sahih' }
                    });
                    verseDataMap[v.verse_key] = vRes.data;
                } catch (err) {
                    verseDataMap[v.verse_key] = null;
                }
            }
            setVerseData(verseDataMap);
        } catch (err) {
            setError('This narrative could not be found. It may have been unpublished or removed.');
        } finally {
            setLoading(false);
        }
    };

    const getSurahName = (verseKey) => {
        const surahId = parseInt(verseKey.split(':')[0]);
        const surah = surahs.find(s => s.id === surahId);
        return surah ? surah.name_simple : '';
    };

    const getArabicText = (verseKey) => {
        const data = verseData[verseKey];
        if (!data?.verse) return '';
        // Concatenate word texts
        const words = data.verse.words || [];
        return words.map(w => w.text_uthmani || w.text || '').join(' ');
    };

    const getTranslation = (verseKey) => {
        const data = verseData[verseKey];
        if (!data?.verse?.translations?.[0]) return '';
        return data.verse.translations[0].text?.replace(/<[^>]*>/g, '') || '';
    };

    const loadComments = async () => {
        if (!topicId) return;
        setLoadingComments(true);
        try {
            const res = await axios.get(`${API}/library/${topicId}/comments`);
            setComments(res.data.comments || []);
        } catch (err) {
            console.error('Failed to load comments:', err);
        } finally {
            setLoadingComments(false);
        }
    };

    const handleAddComment = async (e, parentId = null) => {
        e.preventDefault();
        if (!isAuthenticated) {
            setIsLoginPromptOpen(true);
            return;
        }

        const content = parentId ? replyText : newComment;
        if (!content.trim() || isPosting) return;

        setIsPosting(true);
        try {
            const res = await axios.post(
                `${API}/library/${topicId}/comments`,
                {
                    comment_text: content,
                    reply_to: parentId
                },
                { headers: getAuthHeaders() }
            );
            setComments([...comments, res.data]);
            if (parentId) {
                setReplyText('');
                setReplyingToId(null);
            } else {
                setNewComment('');
            }
        } catch (err) {
            console.error('Failed to post comment:', err);
        } finally {
            setIsPosting(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Delete this comment?')) return;
        try {
            await axios.delete(`${API}/library/${topicId}/comments/${commentId}`, {
                headers: getAuthHeaders()
            });
            // Cascade delete in state: remove the comment and any replies to it
            setComments(comments.filter(c => c.id !== commentId && c.reply_to !== commentId));
        } catch (err) {
            console.error('Failed to delete comment:', err);
        }
    };

    const handleNavigateToVerse = (startIndex) => {
        if (!topic || verses.length === 0) return;
        const mappedVerses = verses.map(v => ({ verse_key: v.verse_key, topic_comment: v.comment }));
        const pseudoWord = { text_uthmani: topic.name, translation: { text: 'Topic' } };
        const targetVerse = mappedVerses[startIndex];
        const [surahId, verseNum] = targetVerse.verse_key.split(':').map(Number);
        startRootBrowsing(pseudoWord, mappedVerses, startIndex, 'topic');
        goToVerse(surahId, verseNum);
        navigate('/');
    };

    const renderParsedText = (text) => {
        if (!text) return null;

        // Match numbers separated by colon, optionally followed by a hyphen and numbers (e.g., 2:255, 19:7-8)
        const regex = /(?<!\d)(\d{1,3}:\d{1,3}(?:-\d{1,3})?)(?!\d)/g;

        const parts = text.split(regex);

        return parts.map((part, index) => {
            // Check if this part matches the verse format exactly
            if (/^\d{1,3}:\d{1,3}(?:-\d{1,3})?$/.test(part)) {
                return <VerseReferenceLink key={index} verseKey={part} />;
            }
            return <React.Fragment key={index}>{part}</React.Fragment>;
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#02040a] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-[#10b981] animate-spin mx-auto mb-4" />
                    <p className="text-[#94a3b8] text-sm">Loading narrative...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#02040a] flex items-center justify-center">
                <div className="text-center max-w-md px-6">
                    <Bookmark className="w-12 h-12 text-[#94a3b8] opacity-30 mx-auto mb-4" />
                    <p className="text-[#f8fafc] font-medium mb-2">Narrative Not Found</p>
                    <p className="text-[#94a3b8] text-sm mb-6">{error}</p>
                    <Link to="/" className="text-[#10b981] hover:text-[#34d399] text-sm underline">← Back to Ai Quran</Link>
                </div>
            </div>
        );
    }

    const pageDescription = verses?.[0]?.comment
        ? verses[0].comment.substring(0, 160) + (verses[0].comment.length > 160 ? '...' : '')
        : `Read the Quranic narrative for ${topic?.name || 'this topic'}`;

    const pageTitle = topic?.name ? `${topic.name} | Ai Quran Narrative` : 'Ai Quran Narrative';

    return (
        <div className="min-h-screen bg-[#02040a]">
            <Helmet title={pageTitle}>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={`https://aiquran.org/narrative/${topicId}`} />
            </Helmet>

            {/* Header */}
            <header className="border-b border-[#1e293b] bg-[#09090b]/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="flex-shrink-0 text-[#94a3b8] hover:text-white transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-[#d4af37] to-[#a16207] flex items-center justify-center">
                                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-[#02040a]" />
                            </div>
                            <h1 className="heading-primary text-lg sm:text-xl text-[#f8fafc]">
                                Ai Quran
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => window.print()}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1e293b] hover:bg-[#334155] text-[#f8fafc] text-xs font-medium transition-colors border border-[#334155] no-print"
                        >
                            <Printer className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Print</span>
                        </button>
                        <button
                            onClick={async () => {
                                const shareData = {
                                    title: `Quran Narrative: ${topic?.name}`,
                                    text: `Read this Quranic narrative: ${topic?.name}`,
                                    url: window.location.href,
                                };
                                try {
                                    if (navigator.share) {
                                        await navigator.share(shareData);
                                    } else {
                                        await navigator.clipboard.writeText(window.location.href);
                                        alert('Link copied to clipboard!');
                                    }
                                } catch (err) {
                                    console.error('Error sharing:', err);
                                }
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1e293b] hover:bg-[#334155] text-[#f8fafc] text-xs font-medium transition-colors border border-[#334155] no-print"
                        >
                            <Share2 className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Share</span>
                        </button>
                    </div>
                </div>
            </header>

            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    .no-print { display: none !important; }
                    
                    /* Force light mode backgrounds */
                    body, .min-h-screen, header, footer { 
                        background: white !important; 
                        background-color: white !important;
                    }
                    
                    /* Force all text to black to fix unreadable light-grey translation/comment text */
                    * { 
                        color: black !important; 
                        text-shadow: none !important;
                    }
                    
                    /* Exception for Gold and Green text colors to keep app identity */
                    .text-\\[\\#d4af37\\] { color: #d4af37 !important; }
                    .text-\\[\\#10b981\\] { color: #10b981 !important; }
                    
                    /* Keep logo background gradient */
                    .from-\\[\\#d4af37\\] { 
                        background: linear-gradient(to bottom right, #d4af37, #a16207) !important; 
                        -webkit-print-color-adjust: exact; 
                        print-color-adjust: exact; 
                    }
                    
                    /* Structural tweaks for paper */
                    article { 
                        page-break-inside: avoid; 
                        border-bottom: 1px solid #ccc !important; 
                        padding-bottom: 2rem; 
                        margin-bottom: 2rem; 
                    }
                    
                    /* Fix border colors for light background */
                    .border-\\[\\#1e293b\\], .border-t, .border-b { border-color: #ccc !important; }
                    
                    /* Verse comment left-border */
                    .border-l-2 { 
                        border-left-color: #10b981 !important; 
                        border-left-style: solid !important;
                    }
                    
                    /* Readability */
                    .arabic-text { line-height: 2 !important; }
                }
            `}} />

            {/* Topic Meta */}
            <div className="max-w-3xl mx-auto px-4 pt-8 pb-4">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#10b981]/10 text-[#10b981] text-xs font-medium mb-4">
                        <Bookmark className="w-3 h-3" /> Quranic Narrative
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-[#f8fafc] mb-4">{topic?.name}</h2>
                    <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-[#94a3b8]">
                        {topic?.author_name && (
                            <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {topic.author_name}</span>
                        )}
                        {topic?.published_at && (
                            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {new Date(topic.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        )}
                        <span className="flex items-center gap-1.5"><Eye className="w-4 h-4" /> {topic?.views?.toLocaleString() || 0} views</span>
                        <span>{verses.length} verse{verses.length !== 1 ? 's' : ''}</span>
                    </div>
                </div>

                {/* Decorative divider */}
                <div className="flex items-center justify-center gap-3 mb-10">
                    <div className="h-px bg-gradient-to-r from-transparent to-[#1e293b] flex-1 max-w-[100px]" />
                    <span className="text-[#d4af37] text-lg">﷽</span>
                    <div className="h-px bg-gradient-to-l from-transparent to-[#1e293b] flex-1 max-w-[100px]" />
                </div>

                {/* Verses */}
                <div className="space-y-10">
                    {verses.map((verse, index) => {
                        const arabicText = getArabicText(verse.verse_key);
                        const translation = getTranslation(verse.verse_key);
                        const surahName = getSurahName(verse.verse_key);

                        return (
                            <article key={verse.id || index} className="group">
                                {/* Surah badge */}
                                <div
                                    className="flex items-center gap-2 mb-3 cursor-pointer group w-fit"
                                    onClick={() => handleNavigateToVerse(index)}
                                    title="Open in main page"
                                >
                                    <span className="px-2.5 py-1 text-xs rounded-full bg-[#10b981]/10 text-[#10b981] font-medium group-hover:bg-[#10b981]/20 transition-colors">
                                        {surahName}
                                    </span>
                                    <span className="text-[#f8fafc] text-sm font-semibold group-hover:text-[#34d399] transition-colors">{verse.verse_key}</span>
                                </div>

                                {/* Arabic text */}
                                {arabicText && (
                                    <p className="arabic-text text-2xl sm:text-2xl text-[#f8fafc] !leading-[3rem] sm:!leading-[3rem] text-right pt-6 sm:pt-2 pb-1 -mb-2 sm:-mb-2 border-b border-[#1e293b]/50" dir="rtl">
                                        {arabicText}
                                    </p>
                                )}

                                {/* Translation */}
                                {translation && (
                                    <p className="text-[#94a3b8] text-sm leading-relaxed mb-3 italic">
                                        {translation}
                                    </p>
                                )}

                                {/* Comment/narration */}
                                {verse.comment && (
                                    <div className="mt-3 pl-4 border-l-2 border-[#10b981]/40">
                                        <div className="whitespace-pre-wrap font-sans text-sm text-[#e2e8f0] leading-relaxed">
                                            {renderParsedText(verse.comment)}
                                        </div>
                                    </div>
                                )}

                                {/* Verse separator */}
                                {index < verses.length - 1 && (
                                    <div className="flex items-center justify-center mt-8">
                                        <div className="flex items-center gap-2">
                                            <div className="w-1 h-1 rounded-full bg-[#334155]" />
                                            <div className="w-1.5 h-1.5 rounded-full bg-[#475569]" />
                                            <div className="w-1 h-1 rounded-full bg-[#334155]" />
                                        </div>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </div>

            {/* More from Author Section */}
            {authorTopics.length > 0 && (
                <div className="max-w-3xl mx-auto px-4 py-10 border-t border-[#1e293b]/30 no-print">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <div className="w-1 h-5 bg-[#d4af37] rounded-full" />
                            <h3 className="text-lg font-bold text-[#f8fafc]">More from this Author</h3>
                        </div>
                        {authorTopics.length > 2 && (
                            <button
                                onClick={() => setShowAllAuthorTopics(!showAllAuthorTopics)}
                                className="text-xs text-[#d4af37] hover:text-[#facc15] font-medium transition-colors"
                            >
                                {showAllAuthorTopics ? 'Show Less' : `View all from author (${authorTopics.length})`}
                            </button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {(showAllAuthorTopics ? authorTopics : authorTopics.slice(0, 2)).map((otherTopic) => (
                            <Link
                                key={otherTopic.id}
                                to={`/narrative/${otherTopic.id}`}
                                className="group p-4 rounded-xl bg-[#0f172a]/30 border border-[#1e293b] hover:border-[#d4af37]/30 transition-all hover:bg-[#0f172a]/60"
                            >
                                <h4 className="text-[#f8fafc] font-medium text-sm mb-2 group-hover:text-[#d4af37] transition-colors line-clamp-1">
                                    {otherTopic.name}
                                </h4>
                                <div className="flex items-center justify-between mt-auto">
                                    <span className="flex items-center gap-1 text-[10px] text-[#475569]">
                                        <Eye className="w-3 h-3" /> {otherTopic.views?.toLocaleString() || 0} views
                                    </span>
                                    <span className="text-[10px] text-[#10b981] font-medium transition-transform group-hover:translate-x-1">Read →</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Comments Section */}
            <div className="max-w-3xl mx-auto px-4 py-12 border-t border-[#1e293b] no-print">
                <div className="flex items-center gap-2 mb-8">
                    <MessageSquare className="w-5 h-5 text-[#d4af37]" />
                    <h2 className="text-xl font-bold text-[#f8fafc]">Conversations</h2>
                    <span className="text-xs text-[#64748b] bg-[#1e293b] px-2 py-0.5 rounded-full">
                        {comments.length}
                    </span>
                </div>

                {/* Comment Input */}
                <div className="bg-[#0f172a]/50 rounded-xl border border-[#1e293b] p-4 mb-8">
                    <form onSubmit={handleAddComment}>
                        <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            onFocus={() => !isAuthenticated && setIsLoginPromptOpen(true)}
                            placeholder="Share your thoughts on this narrative..."
                            className="w-full bg-transparent border-none focus:ring-0 text-[#f8fafc] text-sm resize-none min-h-[80px]"
                            maxLength={1000}
                        />
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#1e293b]/50">
                            <span className="text-[10px] text-[#475569]">
                                {newComment.length}/1000
                            </span>
                            <button
                                type="submit"
                                disabled={!newComment.trim() || isPosting}
                                className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-[#d4af37] hover:bg-[#b8962d] disabled:bg-[#1e293b] disabled:text-[#475569] text-[#02040a] text-xs font-bold transition-all"
                            >
                                {isPosting ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <Send className="w-3.5 h-3.5" />
                                )}
                                Post Comment
                            </button>
                        </div>
                    </form>
                </div>

                {/* Comments List */}
                <div className="space-y-6">
                    {loadingComments ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" />
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="text-center py-12 bg-[#0f172a]/20 rounded-2xl border border-dashed border-[#1e293b]">
                            <MessageSquare className="w-8 h-8 text-[#1e293b] mx-auto mb-3" />
                            <p className="text-[#64748b] text-sm">No comments yet. Be the first to share a thought!</p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {comments.filter(c => !c.reply_to).map((comment) => {
                                const isOwner = currentUser?.user_id === comment.user_id;
                                const isAdmin = currentUser?.is_admin;
                                const replies = comments.filter(r => r.reply_to === comment.id);

                                return (
                                    <div key={comment.id} className="space-y-4">
                                        {/* Main Comment */}
                                        <div className="group flex gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            <div className="flex-shrink-0">
                                                {comment.user_picture ? (
                                                    <img src={comment.user_picture} alt="" className="w-8 h-8 rounded-full border border-[#1e293b]" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-[#1e293b] flex items-center justify-center">
                                                        <User className="w-4 h-4 text-[#94a3b8]" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-grow">
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-bold text-[#f8fafc]">{comment.user_name}</span>
                                                        <span className="text-[10px] text-[#475569]">
                                                            {new Date(comment.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        {(isOwner || isAdmin) && (
                                                            <button
                                                                onClick={() => handleDeleteComment(comment.id)}
                                                                className="p-1.5 text-[#475569] hover:text-[#ef4444] transition-all"
                                                                title="Delete Comment"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-[#f8fafc] text-sm leading-relaxed mb-1 whitespace-pre-wrap">
                                                    {renderParsedText(comment.comment_text)}
                                                </div>

                                                <div className="flex items-center mt-2">
                                                    <button
                                                        onClick={() => {
                                                            if (!isAuthenticated) {
                                                                setIsLoginPromptOpen(true);
                                                            } else {
                                                                setReplyingToId(replyingToId === comment.id ? null : comment.id);
                                                                setReplyText('');
                                                            }
                                                        }}
                                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ${
                                                            replyingToId === comment.id 
                                                            ? 'bg-[#d4af37] text-black' 
                                                            : 'bg-[#1e293b]/50 text-[#94a3b8] hover:bg-[#1e293b] hover:text-[#d4af37]'
                                                        }`}
                                                    >
                                                        <MessageSquare className="w-3.5 h-3.5" />
                                                        Reply
                                                    </button>
                                                </div>

                                                {/* Reply Form */}
                                                {replyingToId === comment.id && (
                                                    <div className="mt-4 animate-in zoom-in-95 duration-200">
                                                        <div className="bg-[#0f172a]/80 rounded-xl border border-[#d4af37]/30 p-3">
                                                            <form onSubmit={(e) => handleAddComment(e, comment.id)}>
                                                                <textarea
                                                                    autoFocus
                                                                    value={replyText}
                                                                    onChange={(e) => setReplyText(e.target.value)}
                                                                    placeholder={`Reply to ${comment.user_name}...`}
                                                                    className="w-full bg-transparent border-none focus:ring-0 text-[#f8fafc] text-sm resize-none min-h-[60px]"
                                                                    maxLength={1000}
                                                                />
                                                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#1e293b]/50">
                                                                    <span className="text-[10px] text-[#475569]">
                                                                        {replyText.length}/1000
                                                                    </span>
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setReplyingToId(null)}
                                                                            className="px-3 py-1 text-[10px] font-bold text-[#94a3b8] hover:text-white transition-colors"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                        <button
                                                                            type="submit"
                                                                            disabled={!replyText.trim() || isPosting}
                                                                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#d4af37] hover:bg-[#b8962d] disabled:bg-[#1e293b] disabled:text-[#475569] text-[#02040a] text-[10px] font-bold transition-all"
                                                                        >
                                                                            {isPosting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                                                            Post Reply
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Replies List */}
                                        {replies.length > 0 && (
                                            <div className="ml-8 space-y-4 border-l border-[#1e293b] pl-4">
                                                {replies.map((reply) => {
                                                    const isReplyOwner = currentUser?.user_id === reply.user_id;
                                                    const isReplyAdmin = currentUser?.is_admin;

                                                    return (
                                                        <div key={reply.id} className="group flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300 relative">
                                                            <div className="absolute -left-4 top-4 w-4 h-px bg-[#1e293b]" />
                                                            <div className="flex-shrink-0">
                                                                {reply.user_picture ? (
                                                                    <img src={reply.user_picture} alt="" className="w-6 h-6 rounded-full border border-[#1e293b]" />
                                                                ) : (
                                                                    <div className="w-6 h-6 rounded-full bg-[#1e293b] flex items-center justify-center">
                                                                        <User className="w-3 h-3 text-[#94a3b8]" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-grow">
                                                                <div className="flex items-center justify-between mb-0.5">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-[11px] font-bold text-[#f8fafc]">{reply.user_name}</span>
                                                                        <span className="text-[9px] text-[#475569]">
                                                                            {new Date(reply.created_at).toLocaleDateString()}
                                                                        </span>
                                                                    </div>
                                                                    {(isReplyOwner || isReplyAdmin) && (
                                                                        <button
                                                                            onClick={() => handleDeleteComment(reply.id)}
                                                                            className="opacity-0 group-hover:opacity-100 p-1 text-[#475569] hover:text-[#ef4444] transition-all"
                                                                            title="Delete Reply"
                                                                        >
                                                                            <Trash2 className="w-3 h-3" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <div className="text-[#94a3b8] text-xs leading-relaxed whitespace-pre-wrap">
                                                                    {renderParsedText(reply.comment_text)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <footer className="border-t border-[#1e293b] mt-16 py-10 px-4">
                <div className="max-w-3xl mx-auto text-center space-y-4">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#d4af37] to-[#a16207] flex items-center justify-center">
                            <BookOpen className="w-3.5 h-3.5 text-[#02040a]" />
                        </div>
                        <span className="text-[#f8fafc] font-semibold">Ai Quran</span>
                    </div>
                    <p className="text-[#94a3b8] text-sm leading-relaxed max-w-md mx-auto">
                        Create your own Quranic Narratives — collect verses, add your reflections, and share your understanding with the community.
                    </p>
                    <div className="pt-2">
                        <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#10b981] hover:bg-[#059669] text-white text-sm font-medium transition-colors">
                            <BookOpen className="w-4 h-4" />
                            Start at AIQuran.org
                        </Link>
                    </div>
                    <p className="text-[#475569] text-xs pt-4">
                        For feedback reach out to{' '}
                        <a href="mailto:info@aiquran.org" className="text-[#d4af37] hover:text-[#facc15] underline">info@aiquran.org</a>
                    </p>
                </div>
            </footer>

            <LoginPromptDialog
                isOpen={isLoginPromptOpen}
                onClose={() => setIsLoginPromptOpen(false)}
                featureName="Commenting"
            />
        </div>
    );
};

export default StoryReader;
